/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Anchor, Card } from '@nexigendigital/nxui'
import { domainAPI } from 'api/domain'
import { DomainContactType } from 'models/domain'
import { Address } from 'models/account'
import { DomainContact, titleCase } from 'helpers/utils'

import DomainContactLightbox from 'containers/domains/lightbox/domainContactLightbox'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function Contacts() {
    const location = useLocation()
    const id = +location.pathname?.split('/')[2]
    const { data: contacts, isLoading } = domainAPI.endpoints.domainContacts.useQueryState(id)
    const [openLightbox, setOpenLightbox] = useState(false)
    const [currentContact, setCurrentContact] = useState(DomainContact)
    const [type, setType] = useState<DomainContactType>('registrant')
    const column = useMemo(() => ([
        {
            Header: 'Contacts',
            accessor: 'contacts'
        },
        {
            accessor: 'name'
        },
        {
            accessor: 'email'
        },
        {
            accessor: 'company'
        },
        {
            accessor: 'action',
            align: 'right'
        }
    ]), [])
    const data = useMemo(() => {
        if (contacts) {
            return Object.entries(contacts).map((contact) => {
                const [type, contactFields] = contact as [DomainContactType, Address]

                return {
                    contacts: `${titleCase(type)} Contact`,
                    name: (
                        <Value>
                            <p>{`${contactFields.firstname} ${contactFields.lastname}`}</p>
                        </Value>
                    ),
                    email: (
                        <Value>
                            <p>{contactFields.email}</p>
                        </Value>
                    ),
                    organisation: (
                        <Value>
                            <p>{contactFields.organisation}</p>
                        </Value>
                    ),
                    action: (
                        <Anchor
                            color={'primary'}
                            onClick={() => {
                                setType(type)
                                setCurrentContact(contactFields)
                                setOpenLightbox(true)
                            }}
                        >
                            Edit
                        </Anchor>
                    )
                }
            })
        }

        return []
    }, [contacts])

    return (
        <>
            <ContactWrapper
                table={{
                    render: {
                        columns: () => column,
                        data: () => data
                    },
                    conditions: {
                        loading: isLoading && 'Loading Domain Contacts...',
                        search: false,
                        pagination: false
                    }
                }}
            />
            <DomainContactLightbox
                type={type}
                contact={currentContact}
                status={openLightbox}
                close={() => setOpenLightbox(false)}
            />
        </>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const ContactWrapper = styled(Card)`
    width: 100%;
`

const Value = styled.article`
    color: ${props => props.theme['black--100']};
`
